// Basic
import { useState } from 'react';
// Store & actions
import useSessionStore from '@/app/store/session';
import useEnvelopesStore from '@/app/store/modules/envelopes';
// Components
import EnvelopesTable from '@/app/modules/envelopes/components/tables/EnvelopesTable';
import ModalDeprogramEnvelopes from '@/app/modules/envelopes/components/modals/deprogramEnvelopes/ModalDeprogramEnvelopes';
import ModalCreateOrUpdateDownloadCode from '@/app/modules/envelopes/components/modals/createOrUpdateDownloadCode/ModalCreateOrUpdateDownloadCode';
import ModalCreateEnvelopeByForm from '@/app/modules/envelopes/components/modals/createEnvelopeByForm/ModalCreateEnvelopeByForm';
import Page from '@/common-utils/components/structure/page/Page';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import EnvelopeImportProps from '@/app/modules/envelopes/components/import/EnvelopeImportProps';
import ModalImport from '@/common-utils/components/renders/dataTable/import/ModalImport';
// Common
import RouteUrls from '../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsEnvelope } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} Envelopes
 */
function CustomPayEnvelopes() {

    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session);
    const storeRefetchEnvelopes = useEnvelopesStore(s => s.storeRefetchEnvelopes);

    // Constants
    const hasCreatePermission = hasRequiredPermissions({
        permissions: userPermissions,
        requiredPermissions: [
            Permissions.CSV_INSERT_POS_REMOTE_DOWNLOAD.name
        ]
    });

    // State
    const [showModalCreateEnvelopeByForm, setShowModalCreateEnvelopeByForm] = useState(false);
    const [showModalCreateEnvelopeByFile, setShowModalCreateEnvelopeByFile] = useState(false);
    const [showModalCreateDownloadCode, setShowModalCreateDownloadCode] = useState(false);
    const [showModalDeprogramEnvelopes, setShowModalDeprogramEnvelopes] = useState(false);

    return (
        <Page>
            <Page.Header
                route={RouteUrls.ENVELOPES}
                routeBase={RouteUrls.ENVELOPES}
            />
            <Page.Body dataCy='page-envelopes'>
                <EnvelopesTable
                    filtersEventName={setFiltersEventName('envelopes')}
                    pageActions={
                        <PageActions actions={
                            [
                                {
                                    isDropdown: true,
                                    visible: true,
                                    trigger: {
                                        icon: ICONS.ADD_ENVELOPES,
                                        entity: 'actions.page.create_envelopes.title',
                                        namespace: nsEnvelope,
                                        visible: true,
                                        disabled: !hasCreatePermission,
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    subActions: [
                                        {
                                            entity: 'actions.page.create_envelopes.select',
                                            namespace: nsEnvelope,
                                            onClick: () => setShowModalCreateEnvelopeByForm(true),
                                            visible: true,
                                            disabled: !hasCreatePermission,
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        },
                                        {
                                            entity: 'actions.page.create_envelopes.upload',
                                            namespace: nsEnvelope,
                                            onClick: () => setShowModalCreateEnvelopeByFile(true),
                                            visible: true,
                                            disabled: !hasCreatePermission,
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        }
                                    ]
                                },
                                {
                                    icon: ICONS.CLOSE,
                                    entity: 'actions.page.deprogram',
                                    namespace: nsEnvelope,
                                    variant: 'critical',
                                    onClick: () => setShowModalDeprogramEnvelopes(true),
                                    visible: true,
                                    disabled: !hasRequiredPermissions({
                                        permissions: userPermissions,
                                        requiredPermissions: [
                                            Permissions.DELETE_REMOTE_DOWNLOADS.name
                                        ]
                                    }),
                                    tooltipForbidden: 'title.permissions.access_denied'
                                }
                            ]}
                            retractable
                        />
                    } />
            </Page.Body>
            {showModalCreateEnvelopeByForm &&
                <ModalCreateEnvelopeByForm
                    isOpen={showModalCreateEnvelopeByForm}
                    setIsOpen={setShowModalCreateEnvelopeByForm}
                />
            }
            {showModalCreateEnvelopeByFile &&
                <ModalImport
                    isOpen={showModalCreateEnvelopeByFile}
                    setIsOpen={setShowModalCreateEnvelopeByFile}
                    importProps={{ ...EnvelopeImportProps, setLaunchRefetch: storeRefetchEnvelopes }}
                />
            }
            {showModalCreateDownloadCode &&
                <ModalCreateOrUpdateDownloadCode
                    isOpen={showModalCreateDownloadCode}
                    setIsOpen={setShowModalCreateDownloadCode}
                />
            }
            {showModalDeprogramEnvelopes &&
                <ModalDeprogramEnvelopes
                    isOpen={showModalDeprogramEnvelopes}
                    setIsOpen={setShowModalDeprogramEnvelopes}
                />
            }
        </Page>
    );
}

export default CustomPayEnvelopes;